<template>
  <div class="shipyard-root">
    <div v-if="!user.id" class="notification is-light">
      Загрузка…
    </div>

    <template v-else>
      <p class="title is-4">Верфь</p>
      <p v-if="atStation && stationName" class="subtitle is-6">
        Станция «{{ stationName }}»
      </p>

      <div v-if="!atStation" class="notification is-warning is-light">
        Верфь доступна только на станции
      </div>

      <div v-else-if="loading" class="notification is-light">
        Загрузка каталога…
      </div>

      <template v-else>
        <div v-if="isOwner" class="box owner-section">
          <p class="subtitle is-6">Управление ценами</p>
          <div
            v-for="item in catalog"
            :key="`price-${item.id}`"
            class="owner-price-row"
          >
            <span class="owner-price-name">{{ item.drawing.name }}</span>
            <div class="field has-addons owner-price-controls">
              <div class="control">
                <input
                  v-model="priceEdits[item.id]"
                  class="input is-small"
                  type="number"
                  min="0"
                  step="1"
                >
              </div>
              <div class="control">
                <button
                  class="button is-small is-info"
                  :class="{ 'is-loading': savingPriceId === item.id }"
                  :disabled="savingPriceId === item.id"
                  @click="savePrice(item)"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="catalog.length === 0" class="notification is-light">
          На этой станции нет кораблей в продаже
        </div>

        <div
          v-for="item in catalog"
          :key="item.id"
          class="card shipyard-item"
        >
          <div class="card-content">
            <div class="columns is-mobile">
              <div class="column is-narrow">
                <figure class="image shipyard-item-image">
                  <img
                    v-if="item.drawing && item.drawing.image"
                    :src="item.drawing.image"
                    :alt="item.drawing.name"
                  >
                </figure>
              </div>
              <div class="column">
                <p class="title is-5">{{ item.drawing.name }}</p>
                <div class="content">
                  <table class="table is-fullwidth is-narrow shipyard-stats-table">
                    <thead>
                      <tr>
                        <th>Цена</th>
                        <th>Прочность</th>
                        <th>Объём</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ item.price }} кр.</td>
                        <td>{{ paramValue(item.drawing, 'strength') }}</td>
                        <td>{{ paramValue(item.drawing, 'volume') }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <button
                    class="button is-primary is-small mt-2"
                    :class="{ 'is-loading': purchasingDrawingId === item.drawing.id }"
                    :disabled="!canPurchase(item) || purchasingDrawingId === item.drawing.id"
                    @click="purchase(item)"
                  >
                    Купить
                  </button>
                  <p v-if="!canAfford(item)" class="help is-danger mt-1">
                    Недостаточно средств
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import ShipyardService from '../services/ShipyardService'

export default {
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      catalog: [],
      atStation: true,
      isOwner: false,
      stationName: '',
      loading: false,
      priceEdits: {},
      savingPriceId: null,
      purchasingDrawingId: null,
    }
  },
  watch: {
    'user.id': {
      immediate: true,
      handler (id) {
        if (id) this.loadCatalog()
      },
    },
  },
  methods: {
    async loadCatalog () {
      this.loading = true
      try {
        const resp = await ShipyardService.getCatalog()
        this.catalog = resp.data || []
        this.atStation = resp.meta ? resp.meta.at_station !== false : true
        this.isOwner = resp.meta ? resp.meta.is_owner === true : false
        this.stationName = resp.meta ? resp.meta.station_name : ''
        this.syncPriceEdits()
      } finally {
        this.loading = false
      }
    },
    syncPriceEdits () {
      const edits = {}
      this.catalog.forEach((item) => {
        edits[item.id] = item.price
      })
      this.priceEdits = edits
    },
    paramValue (drawing, key) {
      const value = drawing?.params?.[key]
      return value != null ? value : '—'
    },
    canAfford (item) {
      return Number(this.user.balance) >= Number(item.price)
    },
    canPurchase (item) {
      return this.canAfford(item) && !this.purchasingDrawingId
    },
    async savePrice (item) {
      this.savingPriceId = item.id
      try {
        const resp = await ShipyardService.updatePrice(item.id, this.priceEdits[item.id])
        const updated = resp.data
        const index = this.catalog.findIndex((entry) => entry.id === item.id)
        if (index !== -1) {
          this.$set(this.catalog, index, updated)
          this.$set(this.priceEdits, item.id, updated.price)
        }
        this.$buefy.toast.open({
          message: `Цена «${item.drawing.name}» обновлена`,
          type: 'is-success',
          duration: 3000,
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.savingPriceId = null
      }
    },
    async purchase (item) {
      const defaultName = item.drawing.name
      const name = window.prompt('Имя корабля:', defaultName)
      if (!name || !name.trim()) return

      this.purchasingDrawingId = item.drawing.id
      try {
        await ShipyardService.purchase(item.drawing.id, name.trim())
        this.$buefy.toast.open({
          message: `Корабль «${name.trim()}» куплен`,
          type: 'is-success',
          duration: 5000,
        })
        this.$root.$emit('user-balance-changed')
        await this.loadCatalog()
      } catch (error) {
        this.showError(error)
      } finally {
        this.purchasingDrawingId = null
      }
    },
    showError (error) {
      const message = error.response?.data?.errors?.[0]?.message || 'Не удалось выполнить операцию'
      this.$buefy.toast.open({
        message,
        type: 'is-danger',
        duration: 5000,
      })
    },
  },
}
</script>

<style scoped>
.shipyard-root {
  max-width: 640px;
  margin: 0 auto;
}

.shipyard-item {
  margin-bottom: 1rem;
}

.shipyard-item-image {
  width: 128px;
  height: 128px;
  flex-shrink: 0;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shipyard-item-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.owner-section {
  margin-bottom: 1.5rem;
}

.owner-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.owner-price-name {
  flex: 1;
}

.owner-price-controls {
  margin-bottom: 0;
}

.shipyard-stats-table {
  margin-bottom: 0.75rem;
}
</style>
